import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./AheadPaywall.module.scss"

/**
 * AheadPaywall — an original, self-contained EXPIRATION / RENEWAL paywall.
 *
 * Divergence brief (claude-paywalls/_ledger.md, concept doc claude-paywalls/ahead.md).
 * The two prior original expiration concepts were Deep Freeze (cold urgency /
 * skeuomorphic ice) and Unbroken (warm calendar-heatmap streak, plan toggle).
 * This one is neither: it's FUTURE-SELF + TWO-ROADS CONTRAST.
 *
 *   - Layout:       a forking "road ahead" that splits at a TODAY gate into a
 *                   Premium lane and a Free lane receding to a horizon. No hero
 *                   image, no card-first, no calendar grid.
 *   - Interaction:  INPUT-DRIVEN SCRUB — drag a traveller up the road; the
 *                   Premium lane fills with light and the year's upcoming
 *                   memory-tiles bloom grey→colour behind it. Plan choice
 *                   re-settles how far the light reaches; auto-glide on load.
 *   - Register:     hopeful / forward-looking, warm cinematic golden-hour.
 *   - Art:          light + photographic; brand blue is the "light of
 *                   protection", amber is the sun/horizon and the "kept" stamp.
 *   - Value frame:  continuity of FUTURE protection — what keeps happening
 *                   automatically vs. what quietly pauses — framed for renewal.
 *
 * Honest: existing photos always stay yours; only ongoing automation pauses if
 * Premium lapses. No fake countdown, no fake scarcity, real savings arithmetic.
 * Yearly is the featured, default plan and lights the whole road to the horizon.
 */

type Pt = [number, number]

// Cubic-bezier control points in a 0..100 viewBox (preserveAspectRatio="none",
// so these map 1:1 to percentages of the stage — HTML overlays land exactly on
// the drawn curve). Both roads start at the same TODAY gate near the bottom.
const GATE: Pt = [50, 91]
const PREMIUM: [Pt, Pt, Pt, Pt] = [GATE, [41, 66], [32, 33], [45, 7]]
const FREE: [Pt, Pt, Pt, Pt] = [GATE, [60, 67], [71, 39], [56, 8]]

function bezier(c: [Pt, Pt, Pt, Pt], t: number): Pt {
  const u = 1 - t
  const a = u * u * u
  const b = 3 * u * u * t
  const d = 3 * u * t * t
  const e = t * t * t
  return [
    a * c[0][0] + b * c[1][0] + d * c[2][0] + e * c[3][0],
    a * c[0][1] + b * c[1][1] + d * c[2][1] + e * c[3][1],
  ]
}

function pathD(c: [Pt, Pt, Pt, Pt]): string {
  return `M ${c[0][0]} ${c[0][1]} C ${c[1][0]} ${c[1][1]} ${c[2][0]} ${c[2][1]} ${c[3][0]} ${c[3][1]}`
}

// Premium milestones: the year's moments that stay kept. `m` = months from now.
// Tiles sit offset to the LEFT of the road so the glowing lane reads through.
const MILESTONES: { t: number; img: string; m: number }[] = [
  { t: 0.16, img: "roadtrip", m: 0 },
  { t: 0.35, img: "child", m: 2 },
  { t: 0.53, img: "friends", m: 5 },
  { t: 0.71, img: "birthday", m: 8 },
  { t: 0.87, img: "beach", m: 11 },
]

// Free-lane moments: the same kind of memories, honestly shown as not auto-kept.
const FREE_TILES: { t: number; img: string }[] = [
  { t: 0.44, img: "couple" },
  { t: 0.69, img: "friends" },
]

const TILE_OFFSET = 15 // viewBox units to the left of the premium road

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

type PlanKey = "yearly" | "threeMonths" | "monthly"

const AheadPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const threeMonthsProduct = premium?.threeMonthsProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const threeMonthsPrice = threeMonthsProduct
    ? formatCurrency(threeMonthsProduct.price, locale)
    : "$29.99"

  // Honest savings vs paying monthly for a year.
  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [progress, setProgress] = useState(reduceMotion ? 1 : 0)
  const [dragging, setDragging] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")

  const stageRef = useRef<HTMLDivElement>(null)
  const animRef = useRef<ReturnType<typeof animate> | null>(null)

  const stopAnim = useCallback(() => {
    animRef.current?.stop()
    animRef.current = null
  }, [])

  const animateTo = useCallback(
    (to: number) => {
      stopAnim()
      if (reduceMotion) {
        setProgress(to)
        return
      }
      setProgress((from) => {
        animRef.current = animate(from, to, {
          duration: 1.15,
          ease: [0.22, 1, 0.36, 1],
          onUpdate: (v) => setProgress(v),
        })
        return from
      })
    },
    [reduceMotion, stopAnim],
  )

  const planCoverage: Record<PlanKey, number> = useMemo(
    () => ({ yearly: 1, threeMonths: 0.3, monthly: 0.12 }),
    [],
  )

  // Auto-glide to the horizon on load — teaches the gesture and previews the
  // yearly payoff (the whole road lit).
  useEffect(() => {
    if (reduceMotion) {
      setProgress(1)
      return
    }
    const id = window.setTimeout(() => animateTo(1), 420)
    return () => {
      window.clearTimeout(id)
      stopAnim()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setProgressFromPointer = useCallback((clientY: number) => {
    const el = stageRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const yPct = ((clientY - rect.top) / rect.height) * 100
    // Bottom of the road (gate) ≈ 91%, horizon ≈ 8%. Up = further ahead.
    const p = (91 - yPct) / (91 - 8)
    setProgress(Math.max(0, Math.min(1, p)))
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      stopAnim()
      setDragging(true)
      e.currentTarget.setPointerCapture(e.pointerId)
      setProgressFromPointer(e.clientY)
    },
    [setProgressFromPointer, stopAnim],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return
      setProgressFromPointer(e.clientY)
    },
    [dragging, setProgressFromPointer],
  )
  const onPointerUp = useCallback(() => setDragging(false), [])

  const selectPlan = useCallback(
    (key: PlanKey) => {
      setPlan(key)
      animateTo(planCoverage[key])
    },
    [animateTo, planCoverage],
  )

  const now = useMemo(() => new Date(), [])
  const monthsAhead = Math.round(progress * 12)
  const coverDate = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() + monthsAhead, 1)
    return { m: MONTHS[d.getMonth()], y: d.getFullYear() }
  }, [now, monthsAhead])

  const traveller = bezier(PREMIUM, progress)

  const selectedProduct =
    plan === "yearly"
      ? yearlyProduct
      : plan === "monthly"
        ? monthlyProduct
        : threeMonthsProduct

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    if (selectedProduct) native.subscribe(selectedProduct)
  }

  const monthLabelFor = (m: number) => {
    const d = new Date(now.getFullYear(), now.getMonth() + m, 1)
    return MONTHS[d.getMonth()]
  }

  return (
    <div className={styles.root}>
      <div className={styles.dawn} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.endingPill}>
          <span className={styles.endingDot} />
          Your Premium is ending
        </span>
        <h1 className={styles.title}>
          Keep the road ahead
          <br />
          <span className={styles.titleAccent}>protected.</span>
        </h1>
        <p className={styles.sub}>
          You've a whole year of moments coming. Renew and every one is backed
          up, synced and kept in full quality — automatically, the second you
          capture it.
        </p>
      </div>

      <div className={styles.readout} aria-live="polite">
        <div className={styles.readoutMain}>
          Protected through{" "}
          <strong>
            {coverDate.m} {coverDate.y}
          </strong>
        </div>
        <div className={styles.readoutSub}>
          {monthsAhead <= 0
            ? "Your protection stops here — drag up the road to see the year ahead"
            : `${monthsAhead} month${monthsAhead === 1 ? "" : "s"} of memories, kept automatically`}
        </div>
      </div>

      {/* THE ROAD — drag the traveller up to travel the year ahead */}
      <div
        className={styles.stage}
        ref={stageRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="slider"
        aria-label="Travel the year ahead"
        aria-valuemin={0}
        aria-valuemax={12}
        aria-valuenow={monthsAhead}
      >
        <div className={styles.sun} aria-hidden="true" />
        <div className={styles.horizonHaze} aria-hidden="true" />

        <svg
          className={styles.road}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="ahead-light" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#2b5ff5" />
              <stop offset="55%" stopColor="#5b7ff8" />
              <stop offset="100%" stopColor="#ffbf72" />
            </linearGradient>
          </defs>

          {/* Free lane — dim, dashed: the road you're also offered, unlit. */}
          <path className={styles.freeRoad} d={pathD(FREE)} pathLength={1} />

          {/* Premium lane — dim base, then the light fills it as you travel. */}
          <path
            className={styles.premRoadBase}
            d={pathD(PREMIUM)}
            pathLength={1}
          />
          <path
            className={styles.premRoadLight}
            d={pathD(PREMIUM)}
            pathLength={1}
            strokeDasharray={1}
            strokeDashoffset={1 - progress}
          />
        </svg>

        {/* lane labels — the fork, just above TODAY */}
        <span className={`${styles.laneLabel} ${styles.laneLabelPrem}`}>
          Premium
        </span>
        <span className={`${styles.laneLabel} ${styles.laneLabelFree}`}>
          Without Premium
        </span>

        {/* Free-lane tiles — same moments, honestly shown as not auto-kept. */}
        {FREE_TILES.map((f, i) => {
          const [x, y] = bezier(FREE, f.t)
          return (
            <div
              key={`free-${i}`}
              className={styles.freeTile}
              style={{
                left: `${x + 9}%`,
                top: `${y}%`,
                backgroundImage: `url(/ahead/${f.img}.jpg)`,
              }}
            >
              <span className={styles.freeTag}>not backed up</span>
            </div>
          )
        })}

        {/* road nodes — where each memory meets the lane */}
        {MILESTONES.map((mile, i) => {
          const [x, y] = bezier(PREMIUM, mile.t)
          const reached = progress >= mile.t - 0.005
          return (
            <span
              key={`node-${i}`}
              className={`${styles.node} ${reached ? styles.nodeOn : ""}`}
              style={{ left: `${x}%`, top: `${y}%` }}
            />
          )
        })}

        {/* Premium milestones — bloom grey→colour as the light reaches them. */}
        {MILESTONES.map((mile, i) => {
          const [x, y] = bezier(PREMIUM, mile.t)
          const reached = progress >= mile.t - 0.005
          return (
            <div
              key={`mile-${i}`}
              className={`${styles.tile} ${reached ? styles.tileOn : ""}`}
              style={{ left: `${x - TILE_OFFSET}%`, top: `${y}%` }}
            >
              <span className={styles.tileMonth}>{monthLabelFor(mile.m)}</span>
              <div
                className={styles.tilePhoto}
                style={{ backgroundImage: `url(/ahead/${mile.img}.jpg)` }}
              >
                <span className={styles.tileKept} aria-hidden={!reached}>
                  <svg viewBox="0 0 24 24" width="9" height="9">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  kept
                </span>
              </div>
            </div>
          )
        })}

        {/* TODAY gate */}
        <div className={styles.gate}>
          <span className={styles.gateLabel}>TODAY</span>
          <span className={styles.gateDot} />
        </div>

        {/* Traveller */}
        <motion.div
          className={`${styles.traveller} ${dragging ? styles.travellerActive : ""}`}
          style={{ left: `${traveller[0]}%`, top: `${traveller[1]}%` }}
        >
          <span className={styles.travellerRing} />
          <span className={styles.travellerCore} />
        </motion.div>

        {!dragging && progress > 0.9 && (
          <div className={styles.dragHint}>
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M12 19V5M6 11l6-6 6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            drag to travel your year
          </div>
        )}
      </div>

      {/* HONEST CONTRAST — what keeps going vs what pauses */}
      <div className={styles.contrast}>
        <div className={`${styles.lane} ${styles.laneGo}`}>
          <span className={styles.laneHead}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Keeps going with Premium
          </span>
          <p>
            Every new photo &amp; video backs up the instant you take it ·
            synced to all your devices · kept at full resolution · locked away,
            private to you.
          </p>
        </div>
        <div className={`${styles.lane} ${styles.lanePause}`}>
          <span className={styles.laneHead}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M9 6v12M15 6v12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            </svg>
            Pauses if it lapses
          </span>
          <p>
            New moments wait instead of backing up, and your devices stop
            syncing.
            <strong>
              {" "}
              Your existing photos always stay — yours, private, on your phone.
            </strong>
          </p>
        </div>
      </div>

      {/* PLANS — yearly featured */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => selectPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyBadge}>
            Covers the whole year ahead
          </span>
          <span className={styles.yearlyRow}>
            <span className={styles.yearlyLeft}>
              <span className={styles.yearlyName}>Yearly</span>
              <span className={styles.yearlyPerMo}>
                {yearlyPerMonth}/mo · billed as {yearlyPrice}/yr
              </span>
            </span>
            <span className={styles.yearlyRight}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>per year</span>
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveTag}>
              Save {savingsPct}% vs {monthlyPrice}/mo monthly
            </span>
          )}
        </button>

        <div className={styles.secondaryPlans}>
          <button
            className={`${styles.mini} ${plan === "threeMonths" ? styles.miniSel : ""}`}
            onClick={() => selectPlan("threeMonths")}
            aria-pressed={plan === "threeMonths"}
          >
            <span className={styles.miniName}>3 months</span>
            <span className={styles.miniPrice}>{threeMonthsPrice}</span>
          </button>
          <button
            className={`${styles.mini} ${plan === "monthly" ? styles.miniSel : ""}`}
            onClick={() => selectPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.miniName}>Monthly</span>
            <span className={styles.miniPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      {/* Sticky CTA dock */}
      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>Keep protecting my memories</span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7-day free trial, then ${yearlyPrice}/yr · cancel anytime`
              : plan === "monthly"
                ? `${monthlyPrice}/mo · cancel anytime`
                : `${threeMonthsPrice} every 3 months · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default AheadPaywall
